import React, { useState } from 'react'
import { Box, Button, Flex, Message, Text } from '@workwhile/ui'
import {
  useUploadCredentialMutation,
  useWorkExperienceFormOptions,
} from 'queries/user'
import { WorkExperienceCredential } from 'api/user'
import { isAfter, startOfDay } from 'date-fns'
import { User } from 'api/typings'

export function CredentialUpload({
  user,
  refreshData,
}: {
  user: User
  refreshData: () => void
}) {
  const [file, setFile] = useState<File | null>(null)
  const [selectedCredential, setSelectedCredential] =
    useState<WorkExperienceCredential | null>(null)
  const [expirationDate, setExpirationDate] = useState<string>('')
  const [expirationError, setExpirationError] = useState<string>('')
  const [showSuccess, setShowSuccess] = useState<boolean>(false)

  // Fetch available credential options from backend
  const { data: formOptions, isLoading: isLoadingOptions } =
    useWorkExperienceFormOptions()

  // Filter credentials to only show those that require document upload
  // Based on the actual backend data structure
  const documentRequiredCredentials =
    formOptions?.skills?.filter((credential) => {
      // Use documentNeeded field if available
      if (credential.documentNeeded !== undefined) {
        return credential.documentNeeded === true
      }

      // Fallback: Check if this is one of the legacy credential requirements
      if (credential.credential && credential.credential !== 'none') {
        return true
      }

      // Based on the actual data, filter by verificationType === 'certificate'
      // This will include forklift certificates, food handlers, alcohol certifications, etc.
      const isCertificate = credential.verificationType === 'certificate'

      // Also include credentials with "Upload Required" in the name
      const hasUploadRequired = credential.name
        .toLowerCase()
        .includes('upload required')

      return isCertificate || hasUploadRequired
    }) || []

  const {
    mutate: uploadCredential,
    error,
    isPending,
  } = useUploadCredentialMutation(user.id ?? '', {
    onSuccess: () => {
      setFile(null)
      setSelectedCredential(null)
      setExpirationDate('')
      setExpirationError('')
      setShowSuccess(true)
      refreshData()

      // Hide success message after 3 seconds
      setTimeout(() => {
        setShowSuccess(false)
      }, 3000)
    },
  })

  // Determine if the selected credential requires expiration date
  // Based on mobile app logic: certificates and experience types need expiration
  const requiresExpiration =
    selectedCredential?.requirementType === 'Certificates & Experience' ||
    selectedCredential?.verificationType === 'certificate'

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile) {
      setFile(selectedFile)
    }
  }

  const handleUpload = () => {
    if (!file || !selectedCredential) {
      return
    }

    // Clear previous expiration error
    setExpirationError('')

    if (requiresExpiration && !expirationDate) {
      setExpirationError('Expiration date is required for this credential')
      return
    }

    if (requiresExpiration && expirationDate) {
      const today = startOfDay(new Date())
      const selectedDate = startOfDay(new Date(expirationDate))

      if (!isAfter(selectedDate, today)) {
        setExpirationError('Expiration date must be in the future')
        return
      }
    }

    uploadCredential({
      file,
      credentialType: selectedCredential.name,
      workerRequirementId: selectedCredential.requirementId.toString(),
      expiresAt: requiresExpiration ? expirationDate : undefined,
    })
  }

  return (
    <Box backgroundColor="offWhite" p={3} mb={2}>
      <Text fontWeight="bold" mb={2}>
        Upload Credential
      </Text>

      <Flex flexDirection="column" gap={2}>
        <Box>
          <Text mb={1}>Credential Type:</Text>
          {isLoadingOptions ? (
            <Text>Loading credential options...</Text>
          ) : documentRequiredCredentials?.length === 0 ? (
            <Text color="gray">
              No credentials requiring document upload are currently available.
            </Text>
          ) : (
            <select
              value={selectedCredential?.requirementId || ''}
              onChange={(e) => {
                const requirementId = parseInt(e.target.value)
                const credential = documentRequiredCredentials?.find(
                  (s) => s.requirementId === requirementId
                )
                setSelectedCredential(credential || null)
              }}
              disabled={isPending}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
              }}
            >
              <option value="">Select credential type...</option>
              {formOptions?.categories?.map((category) => {
                const categoryCredentials = documentRequiredCredentials
                  ?.filter((credential) =>
                    credential?.categories?.includes(category)
                  )
                  .sort((a, b) => a.name.localeCompare(b.name))
                if (categoryCredentials?.length === 0) return null
                return (
                  <optgroup key={category} label={category.toUpperCase()}>
                    {categoryCredentials.map((credential) => (
                      <option
                        key={credential.requirementId}
                        value={credential.requirementId}
                      >
                        {credential.name} ({credential.requirementType})
                      </option>
                    ))}
                  </optgroup>
                )
              })}
              {/* Show document-required credentials without categories */}
              {documentRequiredCredentials
                ?.filter((credential) => credential?.categories?.length === 0)
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((credential) => (
                  <option
                    key={credential.requirementId}
                    value={credential.requirementId}
                  >
                    {credential.name} ({credential.requirementType})
                  </option>
                ))}
            </select>
          )}
        </Box>

        {selectedCredential ? (
          <Box backgroundColor="lightGray" p={2} borderRadius="4px">
            <Text fontSize="sm" fontWeight="bold">
              Selected Credential Details:
            </Text>
            <Text fontSize="sm">Name: {selectedCredential.name}</Text>
            <Text fontSize="sm">
              Type: {selectedCredential.requirementType}
            </Text>
            <Text fontSize="sm">
              Categories: {selectedCredential?.categories?.join(', ') || 'None'}
            </Text>
            <Text fontSize="sm">
              Verification: {selectedCredential.verificationType}
            </Text>
          </Box>
        ) : null}

        {requiresExpiration ? (
          <Box>
            <Text mb={1} color="black">
              <span style={{ color: 'red' }}>*</span> Expiration Date:
            </Text>
            <input
              type="date"
              value={expirationDate}
              onChange={(e) => {
                setExpirationDate(e.target.value)
                // Clear error when user starts typing
                if (expirationError) {
                  setExpirationError('')
                }
              }}
              disabled={isPending}
              required
              style={{
                width: '100%',
                padding: '8px',
                border: expirationError
                  ? '1px solid #e53e3e'
                  : '1px solid #ccc',
                borderRadius: '4px',
              }}
            />
            {expirationError ? (
              <Text fontSize="sm" color="red" mt={1}>
                {expirationError}
              </Text>
            ) : null}
          </Box>
        ) : null}

        <Box>
          <Text mb={1}>Credential File:</Text>
          <input
            type="file"
            accept="image/*,.pdf"
            onChange={handleFileChange}
            disabled={isPending}
            style={{ width: '100%' }}
          />
        </Box>

        {error ? (
          <Message variant="error" mt={2}>
            {error?.message}
          </Message>
        ) : null}

        {showSuccess ? (
          <Message variant="success" mt={2}>
            ✅ Credential uploaded successfully!
          </Message>
        ) : null}

        <Button
          mt={2}
          width="100%"
          loading={isPending}
          disabled={
            !file ||
            !selectedCredential ||
            (requiresExpiration && !expirationDate)
          }
          onClick={handleUpload}
        >
          Upload Credential
        </Button>
      </Flex>
    </Box>
  )
}
